import { Box, Grid, Tooltip, makeStyles } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useQuery } from "@tanstack/react-query";
import componentStylesGeneric from "assets/theme/views/admin/generic.js";
import GenericButton from "components/Buttons/GenericButton";
import LocationHeader from "components/Headers/LocationHeader";
import PageTitleHeader from "components/Headers/PageTitleHeader";
import { getCheckRENTSubmission, getFilteredMapRent } from "crud/rent.crud";
import useFilterSync from "hooks/useFilterSync";
import { useEffect, useRef, useState } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { getReportAction } from "redux/actions/reportsAction";
import { REPORT_FILE_NAME, REPORT_TYPES } from "utils/const";
import FilterSection from "./FilterSection";
import ResultSection from "./ResultSection";

const useStylesGeneric = makeStyles(componentStylesGeneric);

const useStylesLocal = makeStyles((theme) => ({
  headerRow: {
    display: "flex",
    justifyContent: "start",
    alignItems: "center",
    marginBottom: "1rem",
    columnGap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
      rowGap: theme.spacing(1),
    },
  },
  createButton: {
    marginBottom: "37px",
    [theme.breakpoints.down("sm")]: {
      marginBottom: 0,
      width: "100%",
    },
  },
}));

function RENTListInner({ defaultFormValues }) {
  const classes = useStylesGeneric();
  const localClasses = useStylesLocal();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  const dispatch = useDispatch();
  const [messages, setMessages] = useState([]);
  const { watch, getValues, setValue } = useFormContext();

  const submittedFilterRef = useRef(null);
  const [queryParams, setQueryParams] = useState(() => buildPayload(getValues()));

  const pageSize = watch("pageSize") ?? defaultFormValues.pageSize;

  const handleExport = (extension) => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    const fileName = `${REPORT_FILE_NAME.RENT_MAPS_SEARCH_RESULTS}${y}${m}${d}${extension}`;

    const base = submittedFilterRef.current ?? buildPayload(getValues());

    const reportPayload = {
      reportEnumKey: REPORT_TYPES.RENT_MAPS_SEARCH_RESULTS,
      parameters: JSON.stringify({
        name: base?.name ?? "",
        stateId: base?.stateId ?? "",
        startDate: base?.startDate ?? null,
        endDate: base?.endDate ?? null,
      }),
    };

    dispatch(getReportAction(reportPayload, fileName));
  };

  function buildPayload(values, override) {
    const effectivePageIndex = override?.pageIndex ?? values.pageIndex ?? 1;
    const effectivePageSize = override?.pageSize ?? values.pageSize ?? defaultFormValues.pageSize;

    return {
      name: values.name ?? "",
      stateId: values.stateId ?? "",
      startDate: values.startDate ? values.startDate?.format() : null,
      endDate: values.endDate ? values.endDate?.format() : null,
      pageIndex: effectivePageIndex,
      pageSize: effectivePageSize,
    };
  }

  const { data: canSubmitRENT } = useQuery(["getCheckRENTSubmission"], () => getCheckRENTSubmission());

  const rentQuery = useQuery({
    queryKey: ["getFilteredMapRent", queryParams],
    queryFn: () => getFilteredMapRent(queryParams),
    enabled: !!queryParams,
    keepPreviousData: true,
    select: (data) => ({
      totalPages: data?.totalPages || 0,
      totalRecords: data?.totalCount || 0,
      values: data?.data || [],
    }),
  });

  useEffect(() => {
    if (!submittedFilterRef.current) {
      submittedFilterRef.current = queryParams;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterSync = useFilterSync({
    defaultValues: defaultFormValues,
    syncPagination: true,
    pagination: {
      pageIndexKey: "pageIndex",
      pageSizeKey: "pageSize",
      defaultPageSize: defaultFormValues.pageSize,
    },
    onFilterChange: () => {
      const values = getValues();
      const next = buildPayload(values);

      submittedFilterRef.current = next;
      setQueryParams(next);
    },
  });

  useEffect(() => {
    if (canSubmitRENT?.messages) {
      setMessages(canSubmitRENT.messages);
    }
  }, [canSubmitRENT]);

  const createButton = (
    <GenericButton
      size="medium"
      color="tertiary"
      component={Link}
      to="/admin/rent/create"
      disabled={!canSubmitRENT?.canSubmit}
      fullWidth={isSmDown}
      className={localClasses.createButton}
    >
      Criar Mapa RENT
    </GenericButton>
  );

  const tooltipTitle = messages?.length ? (
    <Box display="flex" flexDirection="column">
      {messages.map((msg, idx) => (
        <Box key={idx}>{msg}</Box>
      ))}
    </Box>
  ) : (
    "Não é possível criar um mapa RENT neste momento."
  );

  const createButtonWithTooltip = !canSubmitRENT?.canSubmit ? (
    <Tooltip title={tooltipTitle} arrow>
      <span style={{ width: isSmDown ? "100%" : "auto" }}>{createButton}</span>
    </Tooltip>
  ) : (
    createButton
  );

  return (
    <Grid container classes={{ root: classes.containerRoot }}>
      <LocationHeader section="Lista de Mapas RENT" />

      <Grid item xs={12} md={12} className={localClasses.headerRow}>
        <PageTitleHeader title="Lista de Mapas RENT" small={isSmDown} />
        {createButtonWithTooltip}
      </Grid>

      <Grid item xs={12} md={12}>
        <FilterSection
          exportDisabled={rentQuery.isFetching}
          onExport={handleExport}
          onSubmit={() => {
            const values = getValues();
            const nextFormValues = {
              ...values,
              pageIndex: 1,
              pageSize: values.pageSize ?? defaultFormValues.pageSize,
            };

            if ((values.pageIndex ?? 1) !== 1) setValue("pageIndex", 1);

            filterSync.handleFilterSubmit(nextFormValues);

            const next = buildPayload(nextFormValues, {
              pageIndex: 1,
              pageSize: nextFormValues.pageSize,
            });

            submittedFilterRef.current = next;
            setQueryParams(next);
          }}
          onReset={() => {
            filterSync.handleFilterReset();

            const next = buildPayload(defaultFormValues, {
              pageIndex: 1,
              pageSize: defaultFormValues.pageSize,
            });

            submittedFilterRef.current = next;
            setQueryParams(next);
          }}
        />
      </Grid>

      <Grid item xs={12} md={12}>
        <Box classes={{ root: classes.tableResultBoxRoot }}>
          <ResultSection
            data={rentQuery.data}
            isLoading={rentQuery.isFetching}
            onPageChange={(_, value) => {
              setValue("pageIndex", value);
              filterSync.updatePagination(value, pageSize);

              const base = submittedFilterRef.current ?? buildPayload(getValues());
              const next = {
                ...base,
                pageIndex: value,
                pageSize,
              };

              submittedFilterRef.current = next;
              setQueryParams(next);
            }}
          />
        </Box>
      </Grid>
    </Grid>
  );
}

export default function RENTList() {
  const defaultFormValues = {
    name: "",
    stateId: "",
    startDate: null,
    endDate: null,
    pageIndex: 1,
    pageSize: 15,
  };

  const form = useForm({ defaultValues: defaultFormValues });

  return (
    <FormProvider {...form}>
      <RENTListInner defaultFormValues={defaultFormValues} />
    </FormProvider>
  );
}
